import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Box, Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { usePromoDisplayConfigRules } from '~/hooks/usePromoDisplayConfigRules';

import { PromoMeta } from '~/services/PromoService';

import CopyableField from '../Common/CopyableField';

import PromoDisplayConfigConditions from './PromoDisplayConfigConditions';

type Props = {
  ruleJSON: definitions['RuleJson'] | null;
  promoMeta: PromoMeta;
  promoCodeName: string;
  promoDisplayConfigId: string;
  onRuleJsonUpdate?: (updatedRuleJson: definitions['RuleJson']) => void;
};

type RowData = definitions['RuleJson']['displayOptions'][0] & {
  id: number;
  isNew?: boolean;
};

const PromoDisplayConfigRules = ({
  ruleJSON,
  promoCodeName,
  promoMeta,
  promoDisplayConfigId,
  onRuleJsonUpdate,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    localRuleJSON,
    rows,
    isSaving,
    hasUnsavedChanges,
    setRows,
    updateLocalRuleJSON,
    updateGlobalConditions,
    handleSaveRuleJSON,
    setDisplayOptionKey,
  } = usePromoDisplayConfigRules({
    ruleJSON,
    promoDisplayConfigId,
    onRuleJsonUpdate,
    promoMeta,
  });

  // Simple editing state - just track which row is being edited
  const [editingRowId, setEditingRowId] = useState<number | null>(null);

  // Form for editing row data
  const form = useForm<RowData>({
    defaultValues: {
      id: 0,
      displayOptionKey: '',
      chanceOfDisplay: 50,
      headerTextOverride: '',
      bodyTextOverride: '',
      promoCodeName: '',
      conditions: [],
      isNew: false,
    },
  });

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      const row = rows.find((r) => r.id === id);
      if (row) {
        form.reset({ ...row });
        setEditingRowId(id as number);
      }
    },
    [rows, form],
  );

  const handleSave = useCallback(
    (id: GridRowId) => {
      const formData = form.getValues();

      if (formData.displayOptionKey === '') {
        enqueueSnackbar('Display Option Key is required', { variant: 'error' });
        return;
      }

      const updatedRow = { ...formData, id, isNew: false };
      const updatedRows = rows.map((r) => (r.id === id ? updatedRow : r));

      setRows(updatedRows as RowData[]);
      updateLocalRuleJSON(updatedRows as RowData[]);

      // Update the parent form's ruleJSON state to keep it in sync
      const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
      const updatedRuleJSON = {
        ...(localRuleJSON || { conditions: [] }),
        displayOptions,
      };
      onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);

      setEditingRowId(null);
    },
    [form, rows, updateLocalRuleJSON, enqueueSnackbar, localRuleJSON, onRuleJsonUpdate],
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      handleSave(id);
    },
    [handleSave],
  );

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      form.reset();
      setEditingRowId(null);
      // If it was a new row, remove it
      const row = rows.find((r) => r.id === id);
      if (row?.isNew) {
        const updatedRows = rows.filter((row) => row.id !== id);
        setRows(updatedRows);
        updateLocalRuleJSON(updatedRows);

        // Update the parent form's ruleJSON state to keep it in sync
        const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
        const updatedRuleJSON = {
          ...(localRuleJSON || { conditions: [] }),
          displayOptions,
        };
        onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);
      }
    },
    [rows, setRows, updateLocalRuleJSON, localRuleJSON, onRuleJsonUpdate, form],
  );

  const handleAddNew = useCallback(() => {
    const newId = Math.max(...rows.map((row) => row.id as number), -1) + 1;
    const newRow: RowData = {
      id: newId,
      displayOptionKey: ruleJSON?.displayOptionKey || '',
      chanceOfDisplay: 50,
      headerTextOverride: '',
      bodyTextOverride: '',
      promoCodeName: promoCodeName,
      conditions: [],
      isNew: true,
    };

    form.reset(newRow);
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    setEditingRowId(newId);
    updateLocalRuleJSON(updatedRows);

    // Update the parent form's ruleJSON state to keep it in sync
    const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
    const updatedRuleJSON = {
      ...(localRuleJSON || { conditions: [] }),
      displayOptions,
    };
    onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);
  }, [rows, updateLocalRuleJSON, localRuleJSON, onRuleJsonUpdate, ruleJSON?.displayOptionKey, promoCodeName, form]);

  const handleDelete = useCallback(
    (id: GridRowId) => () => {
      // If we're deleting the currently editing row, exit edit mode first
      if (editingRowId === id) {
        setEditingRowId(null);
        form.reset();
      }

      // Remove from rows
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      updateLocalRuleJSON(updatedRows);

      // Update the parent form's ruleJSON state to keep it in sync
      const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
      const updatedRuleJSON = {
        ...(localRuleJSON || { conditions: [] }),
        displayOptions,
      };
      onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);
    },
    [rows, updateLocalRuleJSON, editingRowId, localRuleJSON, onRuleJsonUpdate, form],
  );

  const getColumns = (): GridColDef<RowData>[] => {
    return [
      {
        field: 'displayOptionKey',
        renderHeader: () => (
          <Typography variant="button">
            Display Option Key*
            <Tooltip title="(optional) Display Option Key a freeform tracking text field that will be used to track when this display option is displayed.">
              <Info />
            </Tooltip>
          </Typography>
        ),
        flex: 1,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const currentRow = rows.find((row) => row.id === params.row.id) || params.row;

          if (isEditing) {
            return (
              <Controller
                name="displayOptionKey"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    key={`displayOptionKey-${params.row.id}`}
                    size="small"
                    {...field}
                    fullWidth
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
              />
            );
          }
          return currentRow.displayOptionKey && <CopyableField value={currentRow.displayOptionKey} />;
        },
      },
      {
        field: 'promoCodeName',
        renderHeader: () => (
          <Typography variant="button">
            Promo Code Name
            <Tooltip title="(optional) Promo Code Name Override is the code name of the promo that will be cloned when the display option is displayed. A setting of 'OFF' will allow the system to track the display option key, but not clone/show a promo">
              <Info />
            </Tooltip>
          </Typography>
        ),
        flex: 1,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const currentRow = rows.find((row) => row.id === params.row.id) || params.row;

          if (isEditing) {
            return (
              <Controller
                name="promoCodeName"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    key={`promoCodeName-${params.row.id}`}
                    size="small"
                    {...field}
                    fullWidth
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
              />
            );
          }
          return <Typography>{currentRow.promoCodeName || '-'}</Typography>;
        },
      },

      {
        field: 'chanceOfDisplay',
        flex: 1,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const currentRow = rows.find((row) => row.id === params.row.id) || params.row;

          if (isEditing) {
            return (
              <Controller
                name="chanceOfDisplay"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    key={`chanceOfDisplay-${params.row.id}`}
                    size="small"
                    type="number"
                    fullWidth
                    {...field}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      field.onChange(value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
              />
            );
          }
          return <Typography>{currentRow.chanceOfDisplay}</Typography>;
        },
        renderHeader: () => (
          <Typography variant="button">
            Chance of Display*
            <Tooltip title="(optional) Chance of Display is the chance that this display option will be displayed. The Sum of all display options will be added then the random number in this range will be generated to determine which display option will be displayed.">
              <Info />
            </Tooltip>
          </Typography>
        ),
      },
      {
        field: 'headerTextOverride',
        renderHeader: () => (
          <Typography variant="button">
            Header Text Override
            <Tooltip title="(optional) Header Text Override is the text that will be displayed when this display option is displayed.">
              <Info />
            </Tooltip>
          </Typography>
        ),
        flex: 1,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const currentRow = rows.find((row) => row.id === params.row.id) || params.row;

          if (isEditing) {
            return (
              <Controller
                name="headerTextOverride"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    key={`headerTextOverride-${params.row.id}`}
                    size="small"
                    {...field}
                    fullWidth
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
              />
            );
          }
          return <Typography>{currentRow.headerTextOverride || '-'}</Typography>;
        },
      },
      {
        field: 'bodyTextOverride',
        renderHeader: () => (
          <Typography variant="button">
            Body Text Override
            <Tooltip title="(optional) Body Text Override is the text that will be displayed when this display option is displayed.">
              <Info />
            </Tooltip>
          </Typography>
        ),
        flex: 1,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const currentRow = rows.find((row) => row.id === params.row.id) || params.row;

          if (isEditing) {
            return (
              <Controller
                name="bodyTextOverride"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    key={`bodyTextOverride-${params.row.id}`}
                    size="small"
                    {...field}
                    fullWidth
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
              />
            );
          }
          return <Typography>{currentRow.bodyTextOverride || '-'}</Typography>;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id }) => {
          const isInEditMode = editingRowId === id;
          const row = rows.find((r) => r.id === id);
          const isNewRow = row?.isNew;

          if (isInEditMode) {
            const actions = [
              <GridActionsCellItem key="save" icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} />,
              <GridActionsCellItem
                key="cancel"
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];

            // Only show delete button for existing rows (not new ones)
            if (!isNewRow) {
              actions.splice(
                1,
                0,
                <GridActionsCellItem
                  key="delete"
                  icon={<DeleteIcon />}
                  label="Delete"
                  onClick={handleDelete(id)}
                  color="error"
                />,
              );
            }

            return actions;
          }

          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
          ];

          return actions;
        },
      },
    ];
  };

  return (
    <>
      <Box>
        <Typography variant="h6" mt={2}>
          Global Conditions
          <Tooltip title="Global Conditions are the conditions that apply to all display options. These conditions need to be met for ANY display option to be displayed.">
            <Info />
          </Tooltip>
        </Typography>

        <PromoDisplayConfigConditions
          conditionType="global"
          conditions={localRuleJSON?.conditions}
          promoMeta={promoMeta}
          onConditionsChange={updateGlobalConditions}
        />
      </Box>
      <Box mt={3}>
        <TextField
          label="Global Display Option Key"
          value={localRuleJSON?.displayOptionKey}
          onChange={(e) => setDisplayOptionKey(e.target.value)}
          fullWidth
        />
        <Stack direction="row" spacing={2} alignItems="center" m={2}>
          <Typography variant="h6">Display Options</Typography>
          <Tooltip
            title="Display Options are the different results that can be displayed to the user. The chance of each result is determined by the chance of display for each display option.
          Setting the 'Promo Code Name Override' to 'OFF' will allow the system to track the display option result, but not clone/show a promo"
          >
            <Info />
          </Tooltip>
          <Box flexGrow={1} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddNew}>
            Add Display Option
          </Button>
        </Stack>

        <DataGrid rows={rows} columns={getColumns()} autoHeight />
      </Box>

      <Box m={2}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            color={hasUnsavedChanges ? 'warning' : 'primary'}
            onClick={handleSaveRuleJSON}
            disabled={isSaving || !localRuleJSON}
          >
            {isSaving ? (
              'Saving...'
            ) : hasUnsavedChanges ? (
              <Tooltip title="Unsaved Changes">
                <Typography variant="body1">Save Rules (Unsaved Changes)</Typography>
              </Tooltip>
            ) : (
              'Save Rules'
            )}
          </Button>
          <CopyableField value={JSON.stringify(localRuleJSON, null, 2)} label="Rules" />
        </Stack>
      </Box>
    </>
  );
};

export default PromoDisplayConfigRules;
